/** Shared transport. The API is same-origin, so every call is a bare path. */

import { translateServerText } from "@/i18n";

const AUTH_PATHS_NO_REFRESH = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/logout",
];

function shouldRefreshOn401(url: string): boolean {
  return !AUTH_PATHS_NO_REFRESH.some((path) => url.includes(path));
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = fetch("/api/auth/refresh", {
    method: "POST",
    credentials: "include",
  })
    .then((res) => res.ok)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/** Same-origin fetch with one silent refresh attempt on 401. */
export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = String(input);
  const opts: RequestInit = { credentials: "include", ...init };

  const res = await fetch(url, opts);
  if (res.status !== 401 || !shouldRefreshOn401(url)) {
    return res;
  }

  if (!(await refreshSession())) {
    return res;
  }

  return fetch(url, opts);
}

export const jsonOpts: RequestInit = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

/** Failed `/api` call: translated message plus status and optional machine code. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

type ErrorBody = {
  detail?: unknown;
  code?: unknown;
};

export async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ErrorBody | null;
    const detail = typeof body?.detail === "string" ? body.detail : null;
    const code = typeof body?.code === "string" ? body.code : null;
    throw new ApiError(
      detail ? translateServerText(detail) : `${res.url} -> ${res.status}`,
      res.status,
      code
    );
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}
